import { clearImagePayloadMemoryStore } from '../navigation/imagePayloadStore';
import { mediaUrlService } from './mediaUrlService';
import { persistentCache } from './persistentCache';
import { processingRealtimeStore } from './processingRealtimeStore';
import { segmentCache } from './segmentCache';
import { uploadQueue } from './uploadQueue';

const USER_SCOPED_LOCAL_STORAGE_PREFIXES = [
  'searchTabState',
  'searchTabState_time',
  'indexTabState',
  'indexTabState_time',
  'playgroundTabState',
  'playgroundTabState_time',
];

interface ClearUserScopedBrowserStateOptions {
  preserveLocalStorageUserId?: string | null;
}

let userScopeTransitionQueue: Promise<void> = Promise.resolve();

const shouldRemoveLocalStorageKey = (key: string, preserveUserId: string | null): boolean => {
  if (key === 'currentUserId') {
    return true;
  }

  return USER_SCOPED_LOCAL_STORAGE_PREFIXES.some((prefix) => {
    if (key === prefix) {
      return true;
    }

    if (!key.startsWith(`${prefix}:`)) {
      return false;
    }

    return !preserveUserId || key !== `${prefix}:${preserveUserId}`;
  });
};

export const clearUserScopedLocalStorage = (preserveUserId: string | null = null): void => {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (shouldRemoveLocalStorageKey(key, preserveUserId)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn('Failed to clear user-scoped localStorage:', error);
  }
};

export const clearUserScopedRuntimeCaches = async (): Promise<void> => {
  mediaUrlService.clearCache();
  processingRealtimeStore.resetForAuthTransition();
  clearImagePayloadMemoryStore();

  // A rejected cache clear is an unsettled identity boundary, not a
  // best-effort cleanup. AuthContext owns the retry/reload recovery surface.
  await segmentCache.clearAll();
};

const performUserScopedBrowserStateClear = async (
  options: ClearUserScopedBrowserStateOptions = {}
): Promise<void> => {
  const nextUserScope = options.preserveLocalStorageUserId || null;
  clearUserScopedLocalStorage(nextUserScope);
  await clearUserScopedRuntimeCaches();
  await persistentCache.clearAll();
};

export const clearUserScopedBrowserState = (
  options: ClearUserScopedBrowserStateOptions = {}
): Promise<void> => {
  const nextUserScope = options.preserveLocalStorageUserId || null;
  // Fence token resolution, streams, and every pending IndexedDB writer at
  // announcement time. The destructive work is serialized below across every
  // AuthProvider instance, so an unmounted provider cannot finish late and
  // erase state created by its successor.
  persistentCache.beginUserScopeTransition(nextUserScope);
  uploadQueue.activateUserScope(null);
  mediaUrlService.beginUserScopeTransition();
  processingRealtimeStore.resetForAuthTransition();

  const transition = userScopeTransitionQueue
    .catch(() => undefined)
    .then(() => performUserScopedBrowserStateClear(options));
  userScopeTransitionQueue = transition.catch(() => undefined);
  return transition;
};

export const waitForUserScopedBrowserStateTransitions = (): Promise<void> => (
  userScopeTransitionQueue
);
